import { editedByEditor } from '@platform/features/modules/settings/form/formSummary';
import { useLabel } from '@platform/hooks/useLabel';
import { findModuleDefinition } from '@platform/modularity/moduleDefinitionRegistry';
import { tileNav } from '@platform/navigation';
import { usePlatformCatalogQuery, useRecordTypeOverridesQuery } from '@platform/state/api';
import { useCustomizationScope } from '../system/useCustomizationScope';
import { globalTypeRows } from './global/globalTypeRows';
import { publishedTypeName } from './global/publishedTypeName';
import type { IFormListRow } from './IFormListRow';
import type { IRecordTypeFlags } from './IRecordTypeFlags';
import { moduleRowStates } from './moduleRowStates';
import { overriddenTypeKeys } from './overriddenTypeKeys';
import { typeRows } from './typeRows';
import { useWorkspaceDefinitions } from './useWorkspaceDefinitions';
import { wsFormSource } from './wsFormSource';

/**
 * Рядки «Форм» (прототип v4 cust-forms): у просторі — форми типів простору в його порядку, пігулка — wsFormSource (те саме правило, що на
 * сторінці модуля й у редакторі форми: «Робочий простір» — власна форма простору, зокрема засіяні картки парламенту; «Глобально» —
 * успадкована глобальна форма); перевизначення читаються лише для типів з `overridden` каталогу. Глобально — 169 форм шаблонів у порядку
 * каталогу (TEMPLATES) з плиткою категорії шаблону («Глобально» — є глобальна форма, інакше «Стандартний»). `loaded` — визначення (і в
 * просторі перевизначення) завантажено.
 */
export function useFormListRows(ws: string): { rows: IFormListRow[]; loaded: boolean; global: boolean } {
  const label = useLabel();
  const scope = useCustomizationScope(ws);
  const global = scope.scope === 'global';
  const { catalog, definitions } = useWorkspaceDefinitions(ws, global);
  const { data: platform } = usePlatformCatalogQuery(undefined, { skip: !global || !scope.canGlobal });
  const keys = global ? '' : overriddenTypeKeys(catalog);
  const { data: overrides, isLoading } = useRecordTypeOverridesQuery({ ws, keys }, { skip: !keys });
  // Патч лише типу, який каталог досі вважає перевизначеним (після «Скинути» старі дані не тримають «Робочий простір»)
  const patchOf = (rt: string) => (keys.split(',').includes(rt) ? overrides?.[rt]?.patch : undefined);
  if (global) {
    const rows = globalTypeRows(definitions ?? [], platform, label, publishedTypeName).map((s): IFormListRow => ({
      row: s.row, name: s.name, tile: s.tile, source: s.globalForm || editedByEditor(s.row.rt) ? 'global' : 'std',
    }));
    return { rows, loaded: !!definitions, global };
  }
  const rows = moduleRowStates(typeRows(definitions ?? []), catalog, label).map(({ row, name }): IFormListRow => {
    const flags = (catalog?.modules.find((m) => m.key === row.def.module.key)?.recordTypes.find((r) => r.key === row.rt.key) ?? {}) as IRecordTypeFlags;
    const published = findModuleDefinition(row.def.module.key)?.recordTypes.find((r) => r.key === row.rt.key);
    return { row, name, tile: tileNav(row.def.module.key, row.rt.key), source: wsFormSource(row.rt, flags, patchOf(row.rt.key), published) };
  });
  return { rows, loaded: !!definitions && !isLoading, global };
}
